/**
 * SIMS-8562 — the rules of a surface's arrangement list, applied to the list as it
 * stands on screen (unsaved order and role edits included), so the author is told
 * before anything is saved.
 *
 * First match wins (Lo-Fi 1f): a viewer gets the first arrangement, in list order,
 * that is unrestricted or names any of their roles (PO ruling 2026-08-26: any of).
 * The server applies the same rule when a surface is opened.
 */

export interface ListedArrangement {
	id: string;
	name: string | null;
	/** Empty: unrestricted, every role. */
	roleIds: string[];
}

export interface AccountRole {
	id: string;
	name: string;
}

/** The arrangement SIMS-8555's first save created has no name until it is renamed. */
export const displayName = (a: Pick<ListedArrangement, "name">): string =>
	a.name ?? "Unnamed arrangement";

const isUnrestricted = (a: ListedArrangement): boolean =>
	a.roleIds.length === 0;

/**
 * AC4: which arrangement a role gets under this order, and its place in the list
 * (1-based). Null: none matches, so that role sees the surface's default.
 */
export const resolveFor = <T extends ListedArrangement>(
	list: readonly T[],
	roleId: string,
): { item: T; position: number } | null => {
	const index = list.findIndex(
		(a) => isUnrestricted(a) || a.roleIds.includes(roleId),
	);
	return index === -1 ? null : { item: list[index], position: index + 1 };
};

export interface Unreachable {
	id: string;
	name: string;
	/** The arrangements above it that already match every role it allows. */
	by: string[];
}

/**
 * AC7, general (Q-5): an arrangement can never be reached when an unrestricted one
 * sits above it, or when every role it allows is already named by arrangements
 * above it (one broader arrangement, or several together). An unrestricted one is
 * reached by every role nothing above names, so only another unrestricted one
 * above it shadows it.
 */
export const unreachableOf = (
	list: readonly ListedArrangement[],
): Unreachable[] => {
	const out: Unreachable[] = [];
	list.forEach((a, i) => {
		const above = list.slice(0, i);
		const open = above.find(isUnrestricted);
		if (open) {
			out.push({ id: a.id, name: displayName(a), by: [displayName(open)] });
			return;
		}
		if (isUnrestricted(a)) return;
		// For each role it allows, the first arrangement above that already takes it.
		const takers = a.roleIds.map((roleId) =>
			above.find((b) => b.roleIds.includes(roleId)),
		);
		if (takers.every((t): t is ListedArrangement => t !== undefined)) {
			const names = takers.map(displayName);
			out.push({
				id: a.id,
				name: displayName(a),
				by: Array.from(new Set(names)),
			});
		}
	});
	return out;
};

const quoted = (names: string[]): string => {
	const q = names.map((n) => `"${n}"`);
	return q.length <= 1
		? q.join("")
		: `${q.slice(0, -1).join(", ")} and ${q[q.length - 1]}`;
};

/** Q-5's copy for AC7's warning, one per unreachable row. */
export const unreachableMessage = (u: Unreachable): string =>
	`"${u.name}" can never be reached — every role it allows is already matched by ${quoted(u.by)} above it.`;

/**
 * AC8: the account's roles that no arrangement is aimed at. They are still served
 * (the first unrestricted arrangement, or the default), so this informs and never
 * blocks. Nothing is said while the surface has no arrangement at all: then every
 * role sees the default, which is AC5 rather than a gap.
 */
export const uncoveredRoles = (
	list: readonly ListedArrangement[],
	roles: readonly AccountRole[],
): AccountRole[] => {
	if (list.length === 0) return [];
	const aimed = new Set(list.flatMap((a) => a.roleIds));
	return roles.filter((r) => !aimed.has(r.id));
};

/** Q-5's copy for AC8's info row. */
export const uncoveredMessage = (
	roles: readonly AccountRole[],
	list: readonly ListedArrangement[],
): string => {
	const open = list.find(isUnrestricted);
	const they = open
		? `They see "${displayName(open)}".`
		: "They see the default view for this surface.";
	return `No arrangement is aimed at: ${roles.map((r) => r.name).join(", ")}. ${they}`;
};
